import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import {
  Button,
  Select,
  Row,
  Input,
  Image,
  Col,
  Tooltip,
  Upload,
  Space,
} from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  CloudUploadOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import GetField from '../Components/GetField';
import stylesCSS from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import InputLeftGrid from '../Components/InputLeftGrid';
import InputRightGrid from '../Components/InputRightGrid';
import InputCentreGrid from '../Components/InputCentreGrid';
import { getCustomers, clearCustomers } from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import StyleForm from './StyleForm';
import {
  getProductTotalA,
  getProductTotalB,
  getProductTotalC,
} from './common-functions';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntInputTextArea = GetField(Input.TextArea);

const CreateCostSheet = props => {
  const { classes } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const [Files, SetFiles] = useState([]);

  const { handleSubmit, control, getValues, watch, setValue } = useForm({
    shouldUnregister: false,
    defaultValues: {
      styles: [
        {
          description: null,
          fabrics: [{ fabric: null }],
          positions: [
            {
              position: 'Front',
              fabric: null,
              price: null,
              avg: null,
              total: null,
            },
            {
              position: 'Back',
              fabric: null,
              price: null,
              avg: null,
              total: null,
            },
            {
              position: 'Piping/Flange',
              fabric: null,
              price: null,
              avg: null,
              total: null,
            },
            {
              position: 'Lining',
              fabric: null,
              price: null,
              avg: null,
              total: null,
            },
            {
              position: 'Patch',
              fabric: null,
              price: null,
              avg: null,
              total: null,
            },
          ],
          processes: [
            { process: 'Printing', qtyAvg: null, price: null, total: null },
            {
              process: 'Garment Dyeing',
              qtyAvg: null,
              price: null,
              total: null,
            },
            { process: 'Quilting', qtyAvg: null, price: null, total: null },
            { process: 'Stitching', qtyAvg: null, price: null, total: null },
            { process: 'CPF', qtyAvg: null, price: null, total: null },
            { process: 'Forwarding', qtyAvg: null, price: null, total: null },
            { process: 'Overhead', qtyAvg: null, price: null, total: null },
            { process: 'Washing', qtyAvg: null, price: null, total: null },
            { process: 'Packaging', qtyAvg: null, price: null, total: null },
            { process: 'Testing', qtyAvg: null, price: null, total: null },
          ],
        },
      ],
    },
  });

  const {
    fields: styleFields,
    append,
    remove: styleRemove,
  } = useFieldArray({
    control,
    name: 'styles',
  });

  const customers = useSelector(state => state.customers);

  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    dispatch(getCustomers());
    return () => {
      dispatch(clearCustomers());
    };
  }, []);

  useEffect(() => {
    const styles = getValues(`styles`);

    setValue(`productTotalA`, getProductTotalA(styles));
    setValue(`productTotalB`, getProductTotalB(styles));
    setValue(`productTotalC`, getProductTotalC(styles));
  }, [styleFields.length]);

  const styleAppend = useCallback(() => {
    append({
      description: null,
      fabrics: [{ fabric: null }],
      positions: [
        {
          position: 'Front',
          fabric: null,
          price: null,
          avg: null,
          total: null,
        },
        {
          position: 'Back',
          fabric: null,
          price: null,
          avg: null,
          total: null,
        },
        {
          position: 'Piping/Flange',
          fabric: null,
          price: null,
          avg: null,
          total: null,
        },
        {
          position: 'Lining',
          fabric: null,
          price: null,
          avg: null,
          total: null,
        },
        {
          position: 'Patch',
          fabric: null,
          price: null,
          avg: null,
          total: null,
        },
      ],
      processes: [
        { process: 'Printing', qtyAvg: null, price: null, total: null },
        {
          process: 'Garment Dyeing',
          qtyAvg: null,
          price: null,
          total: null,
        },
        { process: 'Quilting', qtyAvg: null, price: null, total: null },
        { process: 'Stitching', qtyAvg: null, price: null, total: null },
        { process: 'CPF', qtyAvg: null, price: null, total: null },
        { process: 'Forwarding', qtyAvg: null, price: null, total: null },
        { process: 'Overhead', qtyAvg: null, price: null, total: null },
        { process: 'Washing', qtyAvg: null, price: null, total: null },
        { process: 'Packaging', qtyAvg: null, price: null, total: null },
        { process: 'Testing', qtyAvg: null, price: null, total: null },
      ],
    });
  }, [append]);

  const onSubmit = async formValues => {
    try {
      setConfirmLoading(true);

      const formData = new FormData();

      formData.append('data', JSON.stringify(formValues));

      let index = 0;

      for (const file of Files) {
        formData.append(`files[${index}]`, file);
        index += 1;
      }

      await Api.post('/cost-sheets', formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });

      successNotification('Cost sheet created');
      history.push('/costsheet/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  };

  const beforeUpload = _file => {
    const _Files = Files.slice(0);

    _Files.push(_file);

    SetFiles(_Files);

    return false;
  };

  const onRemove = image => {
    const index = Files.findIndex(file => file === image);

    const _Files = Files.slice(0);

    if (index > -1) {
      _Files.splice(index, 1);
      SetFiles(_Files);
    }
  };

  const renderImage = (image, index) => {
    return (
      <Fragment key={index}>
        <Col xs={24} sm={7} md={6} lg={3} xl={2}>
          <Tooltip
            placement="topLeft"
            title={<DeleteOutlined onClick={() => onRemove(image)} />}
          >
            <Image
              src={URL.createObjectURL(image)}
              className={classes.jobchartImage}
            />
          </Tooltip>
        </Col>
        <Col xs={0} sm={1} />
      </Fragment>
    );
  };

  return (
    <>
      <Breadcrumbs
        moduleName="Cost Sheet"
        moduleLink="/costsheet/list"
        subModuleName="Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="product"
              control={control}
              render={({ field }) => (
                <AntInput label="Product" placeholder="Product" {...field} />
              )}
            />
          </RightGrid>
        </Row>
        <br />
        <Row>
          <Image.PreviewGroup>
            {Files.length ? Files.map(renderImage) : null}
          </Image.PreviewGroup>
        </Row>
        <br />
        <Row>
          <Col xs={4} className={classes.buttonContainer}>
            <Upload beforeUpload={beforeUpload} fileList={[]}>
              <Button icon={<CloudUploadOutlined />}>Select File</Button>
            </Upload>
          </Col>
        </Row>
        <br />
        <Row style={{ padding: 10 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {styleFields.map((item, index) => (
              <StyleForm
                key={item.id}
                styleItem={item}
                styleIndex={index}
                styleRemove={styleRemove}
                getValues={getValues}
                setValue={setValue}
                control={control}
                watch={watch}
              />
            ))}
          </Space>
        </Row>
        <Row>
          <InputLeftGrid>
            <Controller
              name="productTotalA"
              control={control}
              render={({ field }) => (
                <AntInput
                  {...field}
                  label="Total A"
                  placeholder="Total A"
                  type="number"
                  min={0}
                  disabled
                />
              )}
            />
          </InputLeftGrid>
          <InputCentreGrid>
            <Controller
              name="productTotalB"
              control={control}
              render={({ field }) => (
                <AntInput
                  {...field}
                  label="Total B"
                  placeholder="Total B"
                  type="number"
                  min={0}
                  disabled
                />
              )}
            />
          </InputCentreGrid>
          <InputRightGrid>
            <Controller
              name="productTotalC"
              control={control}
              render={({ field }) => (
                <AntInput
                  {...field}
                  label="Total C"
                  placeholder="Total C"
                  type="number"
                  min={0}
                  disabled
                />
              )}
            />
          </InputRightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  label="Remark"
                  allowClear
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" htmlType="button" onClick={styleAppend}>
            <PlusOutlined /> Style
          </Button>
          &nbsp; &nbsp;
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

CreateCostSheet.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(stylesCSS)(CreateCostSheet);
